import { avatarColorFor, initialsOf } from '../lib/format';

interface UserAvatarProps {
  /**
   * Nome humano completo. Iniciais derivadas + cor hashada deterministicamente
   * a partir do `colorKey` (default = name). Mesma combinação → mesma cor.
   */
  name: string | null | undefined;
  /**
   * Chave estável para o hash de cor. Default usa `name`. Em listas onde
   * o mesmo nome pode aparecer múltiplas vezes mas representam pessoas
   * distintas, passar `id` para evitar colisões.
   */
  colorKey?: string;
  /**
   * Tamanho do avatar (default `md` = 32px). `sm`=24px, `lg`=40px.
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Forçar override de iniciais (ex.: "CA" hardcoded). Default deriva do `name`.
   */
  initials?: string;
  /**
   * Forçar override de cor (rgb/hex). Default = `avatarColorFor(colorKey)`.
   */
  color?: string;
  className?: string;
  title?: string;
}

/**
 * UserAvatar canonical — Sprint Visual Brief 2A r7b (Agent A SHELL).
 *
 * Iniciais circulares coloridas com cor determinística por hash do `colorKey`.
 * Render em DS-aligned style (var tokens) sem dependência de imagem.
 *
 * Quando user.avatarUrl materializar via backend, render `<img>` no lugar
 * das iniciais via prop adicional (não escopo Wave atual).
 */
export function UserAvatar({
  name,
  colorKey,
  size = 'md',
  initials: forcedInitials,
  color: forcedColor,
  className,
  title,
}: UserAvatarProps): JSX.Element {
  const safeName = name ?? '';
  const initials = forcedInitials ?? initialsOf(safeName);
  const color = forcedColor ?? avatarColorFor(colorKey ?? safeName);
  const sizeClass =
    size === 'sm' ? 'user-avatar--sm' : size === 'lg' ? 'user-avatar--lg' : 'user-avatar--md';
  return (
    <span
      aria-hidden
      className={['user-avatar', sizeClass, className].filter(Boolean).join(' ')}
      style={{ background: color }}
      title={title ?? safeName}
    >
      {initials}
    </span>
  );
}
